import { PERKS, PerkId, applyPerks as applyPerksGeneric } from "../data/perks";
import type { SkillId } from "../data/skills";
import type { FloorDef } from "../data/floorConfig";
import type { EncounterEnemyConfig } from "./encounterEnemies";
import { initializeRun } from "./floorAdvancement";
import { CharacterFactory, type CharacterId } from "../characters";

export type SkillCardId = "skill_attack" | "skill_slash" | "skill_defend" | "skill_bloodPact";
export type CardId = SkillCardId | PerkId;
export type EnemyId = "goblin" | "bat" | "skeleton" | "slime" | "spider";

/** Skill levels range from 1-5. Level 5 skills are maxed and won't appear in rewards. */
export type SkillLevel = 1 | 2 | 3 | 4 | 5;

export interface PlayerState {
  hp: number;
  maxHp: number;
  atk: number;
  armor: number;
  /** Whether currently defending (reduces incoming damage by armor) */
  isDefending: boolean;
  /** Maps skill ID to its current level (1-5). Skills not yet acquired are absent. */
  skills: Partial<Record<SkillId, SkillLevel>>;
  perks: PerkId[];
}

export interface RunState {
  floor: number;
  player: PlayerState;
  /** Character ID for this run (determines avatar and starting loadout) */
  characterId: CharacterId;
  /** Encounters remaining on current floor */
  encountersRemaining: number;
  /** Encounters cleared on current floor */
  encountersCleared: number;
  /** Active floor configuration (theme, enemy pool, etc.) */
  currentFloorConfig?: FloorDef;
  /** Generated encounters for the current floor */
  currentFloorEncounters: EncounterEnemyConfig[];
  /** Optional seed for reproducible generation */
  seed?: number;
  /** Whether the current encounter is a boss encounter */
  isBossEncounter: boolean;
  /** Whether the run has ended (victory or defeat). */
  runComplete: boolean;
  /** Outcome of the run when complete. */
  runOutcome?: RunOutcome;
  /** Total encounters cleared across the whole run (including bosses). */
  totalEncountersCleared: number;
  /** Total individual enemies defeated across the whole run. */
  totalEnemiesDefeated: number;
}

export type RunOutcome = "victory" | "defeat";

/**
 * Create a new run with the specified character.
 * @param characterId - Character to use for this run (defaults to "warrior")
 * @returns New run state initialized with character stats and skills
 */
export const newRun = (characterId: CharacterId = "warrior"): RunState => {
  // Get character configuration from factory
  const character = CharacterFactory.get(characterId);

  // Build initial skills map from character's starting skills
  const initialSkills: Partial<Record<SkillId, SkillLevel>> = {};
  Object.entries(character.startingSkills).forEach(([skillId, level]) => {
    initialSkills[skillId as SkillId] = level as SkillLevel;
  });

  const run: RunState = {
    floor: 1,
    characterId,
    player: {
      hp: character.stats.maxHp,
      maxHp: character.stats.maxHp,
      atk: character.stats.atk,
      armor: character.stats.armor,
      isDefending: false,
      skills: initialSkills,
      perks: character.startingPerks ? [...character.startingPerks] : [],
    },
    encountersRemaining: 0,
    encountersCleared: 0,
    currentFloorConfig: undefined,
    currentFloorEncounters: [],
    seed: undefined,
    isBossEncounter: false,
    runComplete: false,
    runOutcome: undefined,
    totalEncountersCleared: 0,
    totalEnemiesDefeated: 0,
  };

  // Initialize floor 1 with procedural generation
  initializeRun(run);

  return run;
};

export const cloneRun = (run: RunState): RunState => ({
  floor: run.floor,
  characterId: run.characterId,
  player: {
    hp: run.player.hp,
    maxHp: run.player.maxHp,
    atk: run.player.atk,
    armor: run.player.armor,
    isDefending: run.player.isDefending,
    skills: { ...run.player.skills },
    perks: [...run.player.perks],
  },
  encountersRemaining: run.encountersRemaining,
  encountersCleared: run.encountersCleared,
  currentFloorConfig: run.currentFloorConfig,
  currentFloorEncounters: [...run.currentFloorEncounters],
  seed: run.seed,
  isBossEncounter: run.isBossEncounter,
  runComplete: run.runComplete,
  runOutcome: run.runOutcome,
  totalEncountersCleared: run.totalEncountersCleared,
  totalEnemiesDefeated: run.totalEnemiesDefeated,
});

export const getSkillLevel = (run: RunState, skillId: SkillId): number =>
  run.player.skills[skillId] ?? 0;

export const hasSkill = (run: RunState, skillId: SkillId): boolean =>
  (run.player.skills[skillId] ?? 0) >= 1;

export const isSkillMaxed = (run: RunState, skillId: SkillId): boolean =>
  getSkillLevel(run, skillId) >= 5;

export const levelUpSkill = (run: RunState, skillId: SkillId): void => {
  const currentLevel = getSkillLevel(run, skillId);
  if (currentLevel < 5) {
    run.player.skills[skillId] = (currentLevel + 1) as SkillLevel;
  }
};

export const hasMaxedAllSkills = (run: RunState): boolean => {
  return Object.entries(run.player.skills).every(([_, level]) => level >= 5);
};

export const applyPerk = (run: RunState, perkId: PerkId): void => {
  const perk = PERKS[perkId];
  if (!perk) {
    return;
  }

  const context = {
    addMaxHp: (amount: number) => {
      run.player.maxHp += amount;
    },
    healActor: (amount: number) => {
      run.player.hp = Math.min(run.player.hp + amount, run.player.maxHp);
    },
    addAttack: (amount: number) => {
      run.player.atk += amount;
    },
    addArmor: (amount: number) => {
      run.player.armor += amount;
    },
  };

  perk.apply(context);

  run.player.perks.push(perkId);
};

/**
 * Initialize player perks at run creation (for start with existing perks).
 * This applies the stored perks array to modify base stats.
 * @internal Used during player state initialization.
 */
export const initializePlayerPerks = (player: PlayerState): void => {
  // Only apply if perks array is not empty (avoid re-application)
  if (player.perks.length > 0) {
    applyPerksGeneric(player, player.perks);
  }
};

/**
 * Check if all encounters on the current floor have been cleared.
 * @param run - Current run state
 * @returns True if encountersRemaining is 0
 */
export const isFloorComplete = (run: RunState): boolean => {
  return run.encountersRemaining === 0;
};

/**
 * Decrement encounters remaining after completing an encounter.
 * @param run - Current run state
 */
export const completeEncounter = (run: RunState): void => {
  if (run.encountersRemaining > 0) {
    run.encountersRemaining--;
    run.encountersCleared++;
  }
};

/**
 * Get the maximum floor level (hardcoded to 3 for now).
 * @returns Maximum floor number
 */
export const getMaxFloor = (): number => {
  return 3;
};

/**
 * Check if the run has reached the maximum floor.
 * @param run - Current run state
 * @returns True if current floor is at or above max floor
 */
export const isAtMaxFloor = (run: RunState): boolean => {
  return run.floor >= getMaxFloor();
};

/**
 * Mark the current encounter as a boss encounter.
 * Called when floor is complete and boss should spawn.
 * @param run - Current run state
 */
export const setBossEncounter = (run: RunState): void => {
  run.isBossEncounter = true;
};

/**
 * Clear the boss encounter flag (e.g., after boss is defeated or encounter starts).
 * @param run - Current run state
 */
export const clearBossEncounter = (run: RunState): void => {
  run.isBossEncounter = false;
};

/**
 * Heal the player by a percentage of their max HP.
 * @param run - Current run state
 * @param healPercent - Percentage of max HP to heal (0.0 to 1.0). 1.0 = full heal.
 */
export const healPlayer = (run: RunState, healPercent: number): void => {
  const healAmount = Math.floor(run.player.maxHp * healPercent);
  run.player.hp = Math.min(run.player.hp + healAmount, run.player.maxHp);
};

export const markRunVictory = (run: RunState): void => {
  run.runComplete = true;
  run.runOutcome = "victory";
};

export const markRunDefeat = (run: RunState): void => {
  run.runComplete = true;
  run.runOutcome = "defeat";
};

export const isRunComplete = (run: RunState): boolean => {
  return run.runComplete || run.player.hp <= 0;
};

export const getRunOutcome = (run: RunState): RunOutcome | undefined => {
  if (run.runOutcome) {
    return run.runOutcome;
  }
  if (run.player.hp <= 0) {
    return "defeat";
  }
  return undefined;
};
